/**
 * Protected Layout - Options Oracle V4
 * Wraps authenticated routes with auth checks and common UI
 */
import { createFileRoute, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { AlertTriangle, Clock, Loader2, Scale } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { GlobalMarketStatus } from '../components/GlobalMarketStatus'
import { HeaderControls } from '../components/header/HeaderControls'
import { MobileMenu } from '../components/header/MobileMenu'
import { TopBar } from '../components/header/TopBar'
import { MarketSummaryCards } from '../components/MarketSummaryCards'
import { TradeIntelAnalytics } from '../components/TradeIntelAnalytics'
import { CookieBanner } from '../components/ui/CookieBanner'
import { LegalDisclaimerModal } from '../components/ui/LegalDisclaimerModal'
import { ScrollToTop } from '../components/ui/ScrollToTop'
import { useHistoryController } from '../hooks/useHistoryController'
// FREEZE FIX: useIntradayHistory REMOVED from layout — it caused the ENTIRE
// component tree to re-render whenever history data arrived from the worker.
// TradeIntelAnalytics reads history internally via its own non-reactive mechanism.
import { useMarketStore } from '../services/store'
import { useSettingsStore } from '../stores/settingsStore'
import { GlobalHistoryFetcher } from '../components/GlobalHistoryFetcher'
import { GlobalMarketEngine } from '../components/GlobalMarketEngine'
import { DashboardDataProvider } from '../hooks/useDashboardData'
import { Provider as JotaiProvider } from 'jotai'
import { jotaiStore } from '../stores/liveTickAtoms'
import { GlobalCommandPalette } from '../components/ui/GlobalCommandPalette'
import { Toaster } from '../components/ui/sonner'

export const Route = createFileRoute( '/_auth' )( {
    component: ProtectedLayout,
} )

function ProtectedLayout ()
{
    const navigate = useNavigate()
    const { isAuthenticated, isApproved, isAdmin, loading, userData } = useAuth()

    // Global Settings for Market Data (Optimized Selectors)
    const symbol = useSettingsStore( ( state ) => state.symbol )
    const mode = useSettingsStore( ( state ) => state.mode )
    const range = useSettingsStore( ( state ) => state.range )
    const theme = useSettingsStore( ( state ) => state.theme ) || 'light'
    const isLive = useSettingsStore( ( state ) => state.isLive )
    const historicalDate = useSettingsStore( ( state ) => state.historicalDate )
    const datasetDate = useSettingsStore( ( state ) => state.datasetDate )

    const normalizeDateStr = (d: string | null) => {
        if (!d) return ''
        let str = d
        if (str.includes('T')) str = str.split('T')[0] || str
        if (str.includes(' ')) str = str.split(' ')[0] || str
        const parts = str.split('-')
        if (parts.length === 3 && parts[0] && parts[0].length === 4) {
            return `${parts[2]}-${parts[1]}-${parts[0]}`
        }
        return str
    }
    
    const isDateMismatch = !isLive && datasetDate && normalizeDateStr(datasetDate) !== normalizeDateStr(historicalDate);

    // Theme Sync Effect
    useEffect( () =>
    {
        const root = window.document.documentElement
        root.classList.remove( 'light', 'dark' )
        root.classList.add( theme )

        // Optional: Force background color on body to match theme to prevent white flash
        if ( theme === 'dark' )
        {
            document.body.style.backgroundColor = isDateMismatch ? '#7f1d1d' : '#020617' // slate-950
        } else
        {
            document.body.style.backgroundColor = isDateMismatch ? '#fef2f2' : '#ffffff'
        }
    }, [ theme, isDateMismatch ] )

    // FREEZE FIX: History and RSI are now read INSIDE TradeIntelAnalytics
    // via non-reactive DataBus reads. No more layout-level re-renders.

    // Initialize Turso backup (Legacy: now handled by Cloudflare Worker)
    // useTursoBackup( !loading && isAuthenticated && ( isApproved || isAdmin ) );

    // Initialize Historical Data Controller (Time Jump & Playback)
    useHistoryController()

    // Mobile Menu State
    const [ isMobileMenuOpen, setIsMobileMenuOpen ] = useState( false )

    // Defer heavy components to prioritize First Contentful Paint
    const [ showTradeIntel, setShowTradeIntel ] = useState( false )
    // FREEZE FIX v3: Defer heavy data engines to Phase 2 (after UI shell is painted)
    // Phase 1: Render the visible UI instantly (TopBar, HeaderControls, MarketSummaryCards, Outlet)
    // Phase 2: Mount data engines AFTER the browser has painted the shell
    const [ showEngines, setShowEngines ] = useState( false )
    useEffect( () =>
    {
        let timer: ReturnType<typeof setTimeout> | null = null
        let tradeTimer: ReturnType<typeof setTimeout> | null = null
        // requestAnimationFrame ensures the browser has painted the shell first
        const raf = requestAnimationFrame( () =>
        {
            // Then setTimeout ensures we're in a new macrotask, not blocking paint
            timer = setTimeout( () =>
            {
                setShowEngines( true )
                // TradeIntel is even lower priority — mount 200ms after engines
                tradeTimer = setTimeout( () => setShowTradeIntel( true ), 200 )
            }, 50 )
        } )
        return () => {
            cancelAnimationFrame( raf )
            if ( timer ) clearTimeout( timer )
            if ( tradeTimer ) clearTimeout( tradeTimer )
        }
    }, [] )

    // Redirect handler
    const location = useLocation()
    useEffect( () =>
    {
        if ( !loading && !isAuthenticated )
        {
            const searchStr = location.search && typeof location.search === 'object'
                ? '?' + new URLSearchParams( location.search as Record<string, string> ).toString()
                : typeof location.search === 'string' ? location.search : ''
            const fullRedirect = location.pathname + searchStr

            // console.log( '[Auth] Redirecting to login with return path:', fullRedirect )

            navigate( {
                to: '/login',
                search: { redirect: fullRedirect },
                replace: true,
            } )
        }
    }, [ loading, isAuthenticated, navigate, location.pathname, location.search ] )

    // Main render
    return (
        <div className={`flex flex-col flex-1 relative font-sans min-h-screen transition-colors duration-300 ${isDateMismatch ? 'bg-red-50 dark:bg-red-950' : 'bg-slate-50 dark:bg-slate-950'}`}>
            {isDateMismatch && (
                <div className="fixed inset-0 pointer-events-none border-[8px] sm:border-[12px] border-red-500/90 shadow-[inset_0_0_50px_rgba(239,68,68,0.3)] z-[99999]" />
            )}
            
            {/* Premium Mesh Gradient Background */ }
            <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
                <div className={`absolute inset-0 transition-colors duration-500 ${isDateMismatch ? 'bg-red-50 dark:bg-red-950/90' : 'bg-slate-50 dark:bg-[#04040a]'}`} />
                
                {/* Aurora Mesh Glows */}
                <div className={`absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full blur-[140px] mix-blend-multiply dark:mix-blend-screen opacity-70 ${isDateMismatch ? 'bg-red-300/40 dark:bg-red-900/30' : 'bg-emerald-200/30 dark:bg-emerald-900/20'}`} />
                <div className={`absolute top-[20%] right-[-10%] w-[45%] h-[45%] rounded-full blur-[140px] mix-blend-multiply dark:mix-blend-screen opacity-70 ${isDateMismatch ? 'bg-orange-300/40 dark:bg-orange-900/30' : 'bg-teal-200/30 dark:bg-teal-900/20'}`} />
                <div className={`absolute bottom-[-10%] left-[10%] w-[55%] h-[55%] rounded-full blur-[140px] mix-blend-multiply dark:mix-blend-screen opacity-60 ${isDateMismatch ? 'bg-red-300/40 dark:bg-red-900/20' : 'bg-green-200/30 dark:bg-green-900/15'}`} />
                <div className={`absolute bottom-[20%] right-[20%] w-[40%] h-[40%] rounded-full blur-[140px] mix-blend-multiply dark:mix-blend-screen opacity-50 ${isDateMismatch ? 'bg-orange-200/50 dark:bg-orange-800/20' : 'bg-emerald-100/40 dark:bg-emerald-800/15'}`} />
                
                {/* Ultra-subtle Noise Texture */}
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMjAwIDIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZmlsdGVyIGlkPSJub2lzZUZpbHRlciI+PGZlVHVyYnVsZW5jZSB0eXBlPSJmcmFjdGFsTm9pc2UiIGJhc2VGcmVxdWVuY3k9IjAuODUiIG51bU9jdGF2ZXM9IjMiIHN0aXRjaFRpbGVzPSJzdGl0Y2giLz48L2ZpbHRlcj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWx0ZXI9InVybCgibm9pc2VGaWx0ZXIpIi8+PC9zdmc+')] opacity-[0.02] dark:opacity-[0.035] pointer-events-none mix-blend-overlay"></div>
            </div>

            <div className="relative z-10 w-full max-w-[1920px] mx-auto flex flex-col flex-1">

                <TopBar onOpenSettings={ () => { } } onToggleMobileMenu={ () => setIsMobileMenuOpen( true ) } />
                <MobileMenu isOpen={ isMobileMenuOpen } onClose={ () => setIsMobileMenuOpen( false ) } />
                <HeaderControls />

                <div className="flex-1 flex flex-col">
                    { loading ? (
                        <div className="flex-1 flex items-center justify-center p-8">
                            <div className="flex flex-col items-center gap-4">
                                <Loader2 className="w-10 h-10 text-indigo-500 animate-spin" />
                                <p className="text-slate-500 dark:text-slate-400 font-medium">Loading session...</p>
                            </div>
                        </div>
                    ) : !isAuthenticated ? (
                        <div className="flex-1 flex items-center justify-center p-8">
                            <Loader2 className="w-10 h-10 text-indigo-500 animate-spin" />
                        </div>
                    ) : !isApproved && !isAdmin ? (
                        <div className="flex-1 flex items-center justify-center p-4">
                            <div className="max-w-md w-full bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-800 p-8 text-center">
                                <div className="inline-flex p-4 bg-amber-100 dark:bg-amber-900/30 rounded-2xl mb-6">
                                    <Clock className="w-10 h-10 text-amber-600 dark:text-amber-400" />
                                </div>
                                <h2 className="text-2xl font-black text-slate-800 dark:text-slate-100 mb-2">
                                    Awaiting Approval
                                </h2>
                                <p className="text-slate-500 dark:text-slate-400 mb-6">
                                    Your account is currently { userData?.status || 'awaiting' } administrator approval.
                                    You'll receive full access once approved.
                                </p>
                                <div className="text-xs text-slate-400 mb-4">Signed in as: { userData?.email }</div>
                                <button
                                    onClick={ () => window.location.reload() }
                                    className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold rounded-lg transition-colors text-sm"
                                >
                                    Check Status
                                </button>
                            </div>
                        </div>
                    ) : (
                        <JotaiProvider store={jotaiStore}>
                        <DashboardDataProvider>
                            <div className="px-4 sm:px-6 pt-1 space-y-1 sm:space-y-2">
                                <GlobalMarketStatus />
                                <MarketSummaryCards />
                            </div>
                            <div className="flex-1 min-h-[80vh]">
                                <Outlet />
                            </div>
                            {/* FREEZE FIX v3: Phase 2 — heavy data engines mount AFTER shell is painted */ }
                            { showEngines && (
                                <>
                                    <GlobalMarketEngine />
                                    <GlobalHistoryFetcher />
                                </>
                            ) }
                            { showTradeIntel && (
                                <TradeIntelAnalytics
                                    popupOnly={ true }
                                    suppressPopup={ false }
                                    mode={ mode }
                                    range={ range || 20 }
                                />
                            ) }
                        </DashboardDataProvider>
                        </JotaiProvider>
                    ) }
                </div>
                
                {/* Global Footer / Disclaimer */}
                <div className="max-w-[1400px] mx-auto px-4 md:px-6 mt-6 md:mt-8 pb-8">
                    <div className="relative group">
                        {/* Glow effect */}
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-rose-500/0 via-rose-500/10 to-rose-500/0 rounded-[1.25rem] blur opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                        
                        {/* Content */}
                        <div className="relative bg-white/80 dark:bg-[#090d16]/80 backdrop-blur-xl border border-rose-100 dark:border-rose-900/50 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 transition-colors duration-300 shadow-sm dark:shadow-rose-900/10">
                            
                            {/* Left Side: Badge & Icon */}
                            <div className="flex-shrink-0 flex flex-col items-start gap-2">
                                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase rounded-lg border border-rose-200 dark:border-rose-500/20">
                                    <AlertTriangle className="w-3.5 h-3.5" />
                                    Disclaimer
                                </div>
                                <div className="hidden sm:flex items-center gap-1.5 text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest pl-1">
                                    <Scale className="w-3 h-3" />
                                    Legal Notice
                                </div>
                            </div>
                            
                            {/* Right Side: Message */}
                            <div className="flex flex-col gap-2.5 relative z-10 flex-1 sm:border-l border-rose-100 dark:border-rose-900/30 pl-0 sm:pl-6">
                                <p className="text-[10px] sm:text-[11.5px] font-medium text-slate-600 dark:text-slate-300 leading-relaxed max-w-6xl">
                                    <strong className="text-slate-800 dark:text-slate-100 font-semibold uppercase tracking-wider mr-1.5">Platform Classification:</strong> 
                                    This software functions explicitly as an advanced <span className="text-indigo-600 dark:text-indigo-400 font-semibold">quantitative analytics engine</span> designed solely for structural market analysis, mathematical modeling, and educational purposes. It does <span className="underline decoration-rose-500/40 underline-offset-2">not</span> constitute, nor should it ever be construed as, personalized financial, investment, trading, or legal advice.
                                </p>
                                <p className="text-[10px] sm:text-[11.5px] font-medium text-slate-600 dark:text-slate-300 leading-relaxed max-w-6xl">
                                    <strong className="text-rose-600 dark:text-rose-400 font-semibold uppercase tracking-wider mr-1.5">Extreme Risk Warning:</strong>
                                    Derivatives, options, and margin trading carry a <strong className="text-rose-600 dark:text-rose-400 font-bold">profound risk of total capital loss</strong> and are strictly unsuitable for all investors. Historical market behaviors and algorithmic projections are definitively not indicative of future results. All capital allocations, market executions, and trading strategies implemented using this data are executed <strong className="text-slate-800 dark:text-slate-100 font-bold uppercase tracking-wider bg-rose-50 dark:bg-rose-500/10 px-1 rounded">100% at your absolute discretion and personal risk</strong>.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <CookieBanner />
            <LegalDisclaimerModal />
            <ScrollToTop />
            <GlobalCommandPalette />
            <Toaster position="bottom-right" richColors theme="system" />
        </div>
    )
}
